import { ChangeDetectionStrategy, Component, effect, inject, input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';

import { FlipBookComponent, IconModule } from '@hglobal/common';
import { GoogleAnalyticsService } from '@pt/services';
import { Article } from '@pt/models';
import { TextFormaterHelper } from '@pt/helpers';

@Component({
  selector: 'pu-media-viewer',
  imports: [MatButtonModule, IconModule, FlipBookComponent],
  templateUrl: './media-viewer.component.html',
  styleUrls: ['./media-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaViewerComponent {
  readonly #googleAnalyticsService = inject(GoogleAnalyticsService);

  media = input<string[]>([]);
  pdf = input<string | null>(null);
  article = input<Article | null>(null);

  show = false;
  index = 0;

  flipBookOptions: Record<string, any> = {
    height: '100%',
    googleAnalyticsService: this.#googleAnalyticsService,
  };

  constructor(private _sanitizer: DomSanitizer) {
    effect(() => {
      const art = this.article();
      if (!art) return;

      this.flipBookOptions['categoryName'] = TextFormaterHelper.transformToSnakeCase(
        art.category?.name || '',
      );
      this.flipBookOptions['subcategoryName'] = TextFormaterHelper.transformToSnakeCase(
        art.subcategory?.name || '',
      );
    });
  }

  open(startIndex = 0) {
    this.index = startIndex;
    this.show = true;
  }

  close() {
    this.show = false;
  }

  prev() {
    this.index = (this.index - 1 + this.media().length) % this.media().length;
    this.#sendAnalytic();
  }

  next() {
    this.index = (this.index + 1) % this.media().length;
    this.#sendAnalytic();
  }

  getTransform(): string {
    return `translateX(-${this.index * 100}%)`;
  }

  isYouTube(url: string): boolean {
    return url.includes('youtube.com') || url.includes('youtu.be');
  }

  sanitize(url: string): SafeResourceUrl {
    if (!this.isYouTube(url)) {
      return url as any;
    }
    const match = url.match(/(?:v=|\/)([A-Za-z0-9_-]{11})/);
    const vid = match ? match[1] : '';
    return this._sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube.com/embed/${vid}?rel=0`,
    );
  }

  #sendAnalytic(): void {
    this.#googleAnalyticsService.pushCustomEvent('news', 'materials_navigation', {
      category: this.flipBookOptions['categoryName'],
      subcategory: this.flipBookOptions['subcategoryName'],
      progress: `${this.index + 1}/${this.media().length}`,
    });
  }
}
